import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Card } from '@/design-system/ui/Card';
import { Button } from '@/design-system/ui/Button';
import { Input } from '@/design-system/ui/Field';
import { Icon, type IconName } from '@/design-system/ui/Icon';
import { cn } from '@/design-system/ui/cn';
import { Disclosure } from '@/design-system/ui/Disclosure';
import { Reveal } from '@/design-system/ui/Reveal';
import { toEnglishDigits } from '@/core/format/numbers';
import { isValidMobile } from '@/core/validation/validators';
import { useSEO } from '@/core/seo/useSEO';
import { SITE_NAME, SITE_URL } from '@/core/config/content';
import { HeroWaves } from '../components/HeroWaves';
import { HeroSnow } from '../components/HeroSnow';
import { BlinoAtAGlance } from '../components/BlinoAtAGlance';
import heroTaxi from '@/assets/images/hero_taxi.webp';
import { TripSearchForm } from '../components/TripSearchForm';
import { defaultSearchDate, jalaliToSearchDate, type TripSearch } from '../model/search-params';

const SERVICES: { to: string; icon: IconName; title: string; text: string; badge?: string }[] = [
  { to: '/#search', icon: 'route', title: 'بلیط بین‌شهری', text: 'تاکسی عادی و دربستی بین شهرها' },
  {
    to: '/door-to-door',
    icon: 'pin',
    title: 'سفر درب‌به‌درب',
    text: 'از درِ خانه تا مقصد، اختصاصی',
    badge: 'جدید',
  },
  { to: '/track', icon: 'box', title: 'پیگیری مرسوله', text: 'با کد مرسوله، وضعیت بار را ببینید' },
];

const POPULAR_ROUTES = [
  { from: 'کرمان', to: 'سیرجان', desc: 'سرویس‌های مداوم روزانه' },
  { from: 'کرمان', to: 'رفسنجان', desc: 'حرکت سریع هر نیم ساعت' },
  { from: 'کرمان', to: 'بم', desc: 'سواری عادی و دربستی' },
] as const;

const STEPS = [
  ['۱', 'جست‌وجوی سفر', 'مبدأ، مقصد و تاریخ سفرتان را انتخاب کنید'],
  ['۲', 'انتخاب سرویس', 'گزینه مناسب و صندلی دلخواهتان را انتخاب کنید'],
  ['۳', 'ثبت و پرداخت', 'اطلاعات مسافر را وارد کنید و رزروتان را نهایی کنید'],
] as const;

const HOME_JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      url: SITE_URL,
      name: SITE_NAME,
      potentialAction: [
        {
          '@type': 'SearchAction',
          target: `${SITE_URL}/trips?origin={origin}&destination={destination}`,
          'query-input': 'required name=origin, required name=destination',
        },
        {
          '@type': 'SearchAction',
          target: `${SITE_URL}/track?mode=code&q={tracking_code}`,
          'query-input': 'required name=tracking_code',
        },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'چگونه در بلینو بلیط تاکسی بین‌شهری رزرو کنم؟',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'کافیست در فرم جستجوی بالای صفحه، شهر مبدأ، مقصد و تاریخ سفر را تعیین کرده و سرویس مورد نظر (عادی یا دربستی) را انتخاب و صندلی دلخواه را رزرو کنید.',
          },
        },
        {
          '@type': 'Question',
          name: 'چطور بار یا مرسوله بین‌شهری را رهگیری کنم؟',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'با وارد کردن کد رهگیری پیامک‌شده یا شماره موبایل در تب رهگیری، وضعیت لحظه‌ای مرسوله و موقعیت آن بلافاصله نمایش داده می‌شود.',
          },
        },
        {
          '@type': 'Question',
          name: 'بیمه مرسولات بلینو چقدر است؟',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'تمامی مرسولات ثبت‌شده در سامانه بلینو بدون هزینه اضافی تا سقف ۲ میلیون تومان بیمه می‌شوند.',
          },
        },
        {
          '@type': 'Question',
          name: 'چرا بلینو در حال حاضر فقط در بعضی مسیرها فعال است؟',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'بلینو کار خود را از محورهای اصلی استان کرمان (از جمله کرمان به سیرجان و رفسنجان) آغاز کرده تا سرویس‌دهی را با بالاترین کیفیت راه‌اندازی کند. گسترش به سایر محورها و شهرهای کشور همین حالا در جریان است و به‌زودی کل محورها فعال خواهند شد.',
          },
        },
      ],
    },
  ],
};

type Tab = 'ticket' | 'track';
const TABS: { value: Tab; label: string; icon: IconName }[] = [
  { value: 'ticket', label: 'بلیط بین‌شهری', icon: 'ticket' },
  { value: 'track', label: 'رهگیری مرسوله', icon: 'box' },
];

/** One field for both lookups: an 11-digit number starting with 09 is a mobile, anything else is a load code. */
function QuickTrackForm() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = toEnglishDigits(value.trim());
    if (!v) return setError('کد رهگیری یا شماره موبایل را وارد کنید');
    navigate(`/track?mode=${isValidMobile(v) ? 'mobile' : 'code'}&q=${encodeURIComponent(v)}`);
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <Input
        label="کد مرسوله یا شماره موبایل"
        placeholder="مثال: 05042702001 یا 09121234567"
        hint="با شماره موبایل، همه مرسوله‌های فرستنده یا گیرنده نمایش داده می‌شود"
        inputMode="numeric"
        dir="ltr"
        className="text-start"
        autoComplete="off"
        value={value}
        error={error || undefined}
        onChange={(e) => {
          setValue(e.target.value);
          setError('');
        }}
      />
      <Button type="submit" size="lg" fullWidth icon={<Icon name="search" size={20} />}>
        جستجوی بار
      </Button>
    </form>
  );
}

export default function HomePage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>('ticket');
  const [searchPreset, setSearchPreset] = useState<Partial<TripSearch> | null>(null);
  const [presetKey, setPresetKey] = useState(0);

  const handleSelectRoute = (from: string, to: string) => {
    setTab('ticket');
    setSearchPreset({ origin: from, destination: to });
    setPresetKey((k) => k + 1);
    const searchEl = document.getElementById('search');
    if (searchEl) {
      searchEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const tomorrowDate = jalaliToSearchDate(defaultSearchDate());

  useSEO({
    title: 'خرید آنلاین بلیط تاکسی بین‌شهری، سفر درب‌به‌درب و رهگیری بار',
    description:
      'بلینو؛ سامانه جامع رزرو آنلاین بلیط تاکسی سواری بین‌شهری (عادی و دربستی)، سفر درب‌به‌درب، ارسال و رهگیری سریع بار و مرسوله بین‌شهری در کرمان و سراسر ایران.',
    canonical: '/',
    jsonLd: HOME_JSON_LD,
  });

  // Keep old tracking-site links working: https://blino.ir/?mode=code|mobile&q=<value>
  const legacyMode = params.get('mode');
  const legacyQuery = params.get('q');
  if ((legacyMode === 'code' || legacyMode === 'mobile') && legacyQuery) {
    return (
      <Navigate to={`/track?mode=${legacyMode}&q=${encodeURIComponent(legacyQuery)}`} replace />
    );
  }

  return (
    <div className="pb-10">
      {/* ───────── Hero: gradient + soft wave lines, big two-tone headline, taxi arch ───────── */}
      <section
        className="relative isolate overflow-hidden text-white [background:linear-gradient(225deg,#3d6f96_0%,#2e7a92_48%,#1d8c88_100%)]"
        aria-labelledby="hero-title"
      >
        {/* Always-on ambient light: two slow glowing orbs behind everything. */}
        <div
          className="animate-orb max-sm:animate-none pointer-events-none absolute -start-24 top-10 -z-10 size-[26rem] rounded-full bg-[radial-gradient(circle,rgb(255_228_146/0.28),transparent_68%)]"
          aria-hidden
        />
        <div
          className="animate-orb max-sm:animate-none pointer-events-none absolute end-1/4 -bottom-20 -z-10 size-[28rem] rounded-full bg-[radial-gradient(circle,rgb(2_195_154/0.3),transparent_68%)] [animation-delay:-8s]"
          aria-hidden
        />
        <HeroWaves className="pointer-events-none absolute -z-10 max-w-none" />

        <div className="mx-auto grid max-w-5xl items-center gap-6 px-4 pt-6 pb-24 sm:gap-10 sm:px-8 sm:pt-14 sm:pb-28 lg:grid-cols-2 lg:gap-20 lg:px-12 lg:pt-14 lg:pb-36">
          <div className="flex flex-col items-start text-right">
            <h1
              id="hero-title"
              className="animate-rise text-[1.75rem] leading-[1.75] font-extrabold text-balance min-[380px]:text-[2rem] sm:text-5xl sm:leading-[1.65] lg:text-[3.4rem]"
            >
              با بلینو
              <br className="hidden sm:block" />{' '}
              <span className="text-navy-deep">هوشمند سفر کن،</span>
              <br />
              نه پرهزینه!
            </h1>

            <p className="animate-rise mt-2 max-w-md text-[15px] leading-loose text-white/90 [animation-delay:80ms] sm:mt-4 sm:text-base">
              رزرو سریع تاکسی بین‌شهری با بهترین قیمت
            </p>

            <div className="animate-rise mt-8 hidden w-full flex-wrap items-center justify-start gap-x-6 gap-y-3 [animation-delay:160ms] sm:flex">
              <a
                href="#search"
                onClick={(e) => {
                  e.preventDefault();
                  setTab('ticket');
                  document
                    .getElementById('search')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="text-navy-deep shadow-floating inline-flex h-12 items-center gap-2 rounded-xl bg-white px-7 text-sm font-bold transition hover:bg-white/90 active:scale-95"
              >
                <Icon name="search" size={18} />
                <span>جستجو و رزرو بلیط</span>
              </a>
            </div>
          </div>
          {/* The picture sits on the right in RTL, like the reference. Desktop only: phones go straight to the search card. */}
          <div className="animate-arch-in hidden lg:block">
            <div className="animate-hero-float relative mx-auto w-full max-w-[26rem]">
              {/* Warm light that breathes behind the arch. */}
              <div
                className="animate-glow absolute inset-x-[-12%] top-[18%] bottom-[-6%] -z-10 rounded-full bg-[radial-gradient(circle,rgb(255_228_146/0.55),transparent_68%)] blur-2xl"
                aria-hidden
              />
              <img
                src={heroTaxi}
                alt="تاکسی زرد در خیابانی برفی و شبانه"
                width={900}
                height={996}
                fetchPriority="high"
                className="h-auto w-full drop-shadow-[0_30px_40px_rgb(4_15_27/0.45)]"
              />
              <HeroSnow src={heroTaxi} />
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto -mt-20 max-w-6xl px-4 lg:-mt-24">
        <Card
          id="search"
          className="animate-rise shadow-floating mx-auto max-w-3xl scroll-mt-24 overflow-hidden [animation-delay:300ms]"
        >
          <h2 className="sr-only">جستجو</h2>
          <div
            role="tablist"
            aria-label="خدمت مورد نظر"
            className="border-line bg-fill/60 grid grid-cols-2 border-b"
          >
            {TABS.map((t) => (
              <button
                key={t.value}
                id={`home-tab-${t.value}`}
                role="tab"
                type="button"
                aria-selected={tab === t.value}
                aria-controls={`home-panel-${t.value}`}
                onClick={() => setTab(t.value)}
                className={cn(
                  'relative flex items-center justify-center gap-2 py-3.5 text-sm font-bold transition-colors',
                  tab === t.value ? 'bg-paper text-navy' : 'text-ink-2 hover:text-navy',
                )}
              >
                <Icon
                  name={t.icon}
                  size={19}
                  className={cn(
                    'transition-transform duration-300',
                    tab === t.value && 'text-action scale-110',
                  )}
                />
                {t.label}
                <span
                  aria-hidden
                  className={cn(
                    'bg-cta absolute inset-x-6 bottom-0 h-0.5 origin-center rounded-full transition-[transform,opacity] duration-300',
                    tab === t.value ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0',
                  )}
                />
              </button>
            ))}
          </div>
          <div
            id={`home-panel-${tab}`}
            role="tabpanel"
            aria-labelledby={`home-tab-${tab}`}
            key={tab}
            className="animate-fade-in p-4 sm:p-6"
          >
            {tab === 'ticket' ? (
              <TripSearchForm key={presetKey} initial={searchPreset ?? undefined} />
            ) : (
              <QuickTrackForm />
            )}
          </div>
        </Card>

        <section className="mt-14" aria-labelledby="services">
          <Reveal className="text-center">
            <div className="mb-1 flex items-center justify-center gap-3">
              <span className="bg-line h-0.5 w-8 rounded-full sm:w-12" aria-hidden />
              <h2 id="services" className="text-xl font-bold sm:text-2xl">
                خدمات
              </h2>
              <span className="bg-line h-0.5 w-8 rounded-full sm:w-12" aria-hidden />
            </div>
            <p className="text-ink-2 mb-6 text-sm">سفر و ارسال بین‌شهری</p>
          </Reveal>
          <ul className="grid gap-4 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal as="li" key={s.title} delay={i * 90}>
                <Link to={s.to} className="group block h-full rounded-[16px]">
                  <Card
                    interactive
                    className="flex h-full items-center gap-4 border-transparent p-4"
                  >
                    <span className="bg-primary-light text-navy group-hover:bg-cta group-hover:shadow-glow grid size-12 shrink-0 place-items-center rounded-md transition duration-200 group-hover:scale-110 group-hover:-rotate-6 group-hover:text-white">
                      <Icon name={s.icon} />
                    </span>
                    <span className="flex-1">
                      <span className="flex items-center gap-2 font-bold">
                        {s.title}
                        {s.badge && (
                          <Badge tone="warning" className="text-[11px]">
                            {s.badge}
                          </Badge>
                        )}
                      </span>
                      <span className="text-ink-2 block text-sm">{s.text}</span>
                    </span>
                    <Icon
                      name="chevronLeft"
                      size={18}
                      className="text-ink-3 group-hover:text-action shrink-0 transition duration-200 group-hover:-translate-x-1"
                    />
                  </Card>
                </Link>
              </Reveal>
            ))}
          </ul>
        </section>
      </div>

      {/* Full-bleed band: spans the whole page width, content inside stays centred. */}
      <BlinoAtAGlance />

      <div className="mx-auto max-w-6xl px-4">
        <section className="mt-14" aria-labelledby="steps">
          <Reveal className="text-center">
            <div className="mb-8 flex items-center justify-center gap-3">
              <span className="bg-line h-0.5 w-8 rounded-full sm:w-12" aria-hidden />
              <h2 id="steps" className="text-xl font-bold sm:text-2xl">
                مراحل رزرو
              </h2>
              <span className="bg-line h-0.5 w-8 rounded-full sm:w-12" aria-hidden />
            </div>
          </Reveal>
          <ol className="relative grid gap-6 md:grid-cols-3">
            <span
              className="border-line absolute inset-x-[16.6%] top-6 hidden border-t-2 border-dashed md:block"
              aria-hidden
            />
            {STEPS.map(([n, t, d], i) => (
              <Reveal
                as="li"
                key={n}
                delay={i * 160}
                className="group relative flex items-start gap-4 md:flex-col md:items-center md:text-center"
              >
                <span className="bg-cta shadow-glow ring-bg grid size-12 shrink-0 place-items-center rounded-full text-lg font-bold text-white ring-4 transition duration-300 group-hover:scale-110 group-hover:-rotate-6">
                  {n}
                </span>
                <div>
                  <p className="font-bold">{t}</p>
                  <p className="text-ink-2 mt-0.5 text-sm">{d}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </section>

        {/* Popular Intercity Routes */}
        <section className="mt-14" aria-labelledby="routes-heading">
          <Reveal>
            <div className="flex flex-col items-center gap-3 text-center">
              <div>
                <div className="flex items-center justify-center gap-3">
                  <span className="bg-line h-0.5 w-8 rounded-full sm:w-12" aria-hidden />
                  <h2 id="routes-heading" className="text-xl font-bold sm:text-2xl">
                    مسیرهای پرتردد بین‌شهری
                  </h2>
                  <span className="bg-line h-0.5 w-8 rounded-full sm:w-12" aria-hidden />
                </div>
                <p className="text-ink-2 mt-1 text-sm">سفرهای روزانه و منظم سواری‌های بین‌شهری</p>
              </div>
              <a
                href="#search"
                onClick={(e) => {
                  e.preventDefault();
                  setTab('ticket');
                  document
                    .getElementById('search')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="text-action text-sm font-bold hover:underline"
              >
                جستجوی همه مسیرها
              </a>
            </div>
          </Reveal>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {POPULAR_ROUTES.map((route, i) => (
              <Reveal key={`${route.from}-${route.to}`} delay={i * 90}>
                <Link
                  to={`/trips?origin=${encodeURIComponent(route.from)}&destination=${encodeURIComponent(route.to)}&date=${tomorrowDate}&passengers=1&type=normal`}
                  onClick={(e) => {
                    if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                      e.preventDefault();
                      handleSelectRoute(route.from, route.to);
                    }
                  }}
                  className="group block h-full rounded-[20px]"
                  title={`رزرو سواری بین‌شهری ${route.from} به ${route.to}`}
                >
                  <Card
                    interactive
                    className="group-hover:border-action/40 flex h-full flex-col gap-5 p-5 transition-all duration-200 group-hover:shadow-md"
                  >
                    {/* Route line: origin ●┄┄ car ┄┄● destination */}
                    <div className="flex items-center gap-3">
                      <span className="text-ink-1 text-lg font-extrabold">{route.from}</span>
                      <span className="relative flex h-8 min-w-0 flex-1 items-center" aria-hidden>
                        <span className="border-line absolute inset-x-0 top-1/2 border-t-2 border-dashed" />
                        <span className="bg-primary-light text-navy ring-paper group-hover:bg-cta relative mx-auto grid size-8 place-items-center rounded-full ring-4 transition duration-300 group-hover:-translate-x-2 group-hover:text-white">
                          <Icon name="car" size={16} />
                        </span>
                      </span>
                      <span className="text-ink-1 text-lg font-extrabold">{route.to}</span>
                    </div>

                    <p className="text-ink-2 text-sm">{route.desc}</p>

                    <span className="bg-fill text-navy group-hover:bg-primary-light group-hover:text-action mt-auto inline-flex items-center justify-between rounded-xl px-4 py-2.5 text-sm font-bold transition-colors">
                      <span>انتخاب مسیر</span>
                      <Icon
                        name="chevronLeft"
                        size={16}
                        className="transition-transform group-hover:-translate-x-1"
                      />
                    </span>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>

        {/* GEO & AEO Knowledge Section: Why Blino & Direct Answers */}
        <section
          className="bg-paper mt-16 rounded-[24px] p-6 shadow-sm md:p-8"
          aria-labelledby="geo-heading"
        >
          <Reveal>
            <span className="bg-primary-light text-navy inline-block rounded-full px-3 py-1 text-xs font-bold">
              اکوسیستم هوشمند بلینو
            </span>
            <h2 id="geo-heading" className="mt-2 text-xl font-bold md:text-2xl">
              چرا مسافران و صاحبان بار بلینو را انتخاب می‌کنند؟
            </h2>
            <p className="text-ink-2 mt-2 max-w-2xl text-sm leading-relaxed">
              بلینو سامانه‌ای دیجیتال و یکپارچه برای سفرهای سواری بین‌شهری، ارسال سریع بسته‌ها و
              ارتباط مستقیم میان مسافر، راننده و شرکت‌های حمل‌ونقل است.
            </p>
          </Reveal>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <Card className="p-5">
              <span className="bg-mint/15 text-navy grid size-10 place-items-center rounded-lg">
                <Icon name="ticket" size={20} />
              </span>
              <h3 className="mt-3 font-bold">رزرو آنلاین با صندلی مشخص</h3>
              <p className="text-ink-2 mt-1.5 text-xs leading-relaxed">
                نیازی به ساعت‌ها معطلی در ترمینال نیست. صندلی سواری خود را از قبل رزرو کرده و رأس
                ساعت حرکت کنید.
              </p>
            </Card>

            <Card className="p-5">
              <span className="bg-primary-light text-navy grid size-10 place-items-center rounded-lg">
                <Icon name="shield" size={20} />
              </span>
              <h3 className="mt-3 font-bold">ارسال سریع و بیمه بار</h3>
              <p className="text-ink-2 mt-1.5 text-xs leading-relaxed">
                مرسولات با خودروهای بین‌شهری خطی بین ۱ تا ۳ ساعته می‌رسند و تمامی بسته‌ها تا سقف ۲
                میلیون تومان بیمه رایگان دارند.
              </p>
            </Card>

            <Card className="p-5">
              <span className="bg-action/15 text-action grid size-10 place-items-center rounded-lg">
                <Icon name="pin" size={20} />
              </span>
              <h3 className="mt-3 font-bold">سفر درب‌به‌درب اختصاصی</h3>
              <p className="text-ink-2 mt-1.5 text-xs leading-relaxed">
                حرکت از درب منزل یا محل کار شما در شهر مبدأ و پیاده شدن در مقصد مورد نظرتان بدون
                واسطه.
              </p>
            </Card>
          </div>

          {/* Quick FAQ Accordion for AEO */}
          <div className="border-line mt-8 border-t pt-6">
            <h3 className="text-ink-1 text-base font-bold">سوالات پرتکرار مسافران</h3>
            <div className="mt-4 flex flex-col gap-3">
              <Disclosure summary="چگونه در بلینو بلیط تاکسی بین‌شهری بخرم؟" defaultOpen={true}>
                <p className="text-ink-2 text-sm leading-relaxed">
                  در فرم جستجوی بالای همین صفحه، مبدأ، مقصد و تاریخ حرکت را تعیین کرده و روی دکمه
                  «جستجوی بلیط» بزنید. سپس با انتخاب صندلی دلخواه و پرداخت آنلاین، بلیط شما فوراً
                  صادر خواهد شد.
                </p>
              </Disclosure>

              <Disclosure summary="تفاوت سفر عادی و دربستی چیست؟">
                <p className="text-ink-2 text-sm leading-relaxed">
                  در سفر عادی، هزینه هر صندلی به صورت جداگانه پرداخت می‌شود و سایر صندلی‌ها در
                  اختیار مسافران دیگر قرار می‌گیرد. در سفر اختصاصی (دربستی)، کل ظرفیت خودرو سواری (۳
                  تا ۴ صندلی) بدون مسافر متفرقه در اختیار شما خواهد بود.
                </p>
              </Disclosure>

              <Disclosure summary="قوانین استرداد و کنسلی بلیط چگونه است؟">
                <p className="text-ink-2 text-sm leading-relaxed">
                  از زمان خرید تا یک ساعت پیش از حرکت با کسر ۱۰ درصد، و در فاصله یک ساعت مانده به
                  حرکت با کسر ۵۰ درصد جریمه قابل استرداد است و مبلغ عودتی حداکثر ظرف ۴۸ ساعت به حساب
                  شما بازمی‌گردد.
                </p>
              </Disclosure>

              <Disclosure summary="چرا بلینو فعلاً فقط در بعضی مسیرها فعال است؟">
                <p className="text-ink-2 text-sm leading-relaxed">
                  بلینو کار خود را از محورهای اصلی استان کرمان — از جمله کرمان به سیرجان و رفسنجان
                  — آغاز کرده تا سرویس‌دهی را با بالاترین کیفیت راه‌اندازی کند. گسترش به سایر محورها
                  و شهرهای کشور همین حالا در جریان است و به‌زودی کل محورها فعال خواهند شد.
                </p>
              </Disclosure>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/faq"
                className="text-action inline-flex items-center gap-1.5 text-xs font-bold hover:underline"
              >
                <span>مشاهده تمام سوالات متداول و قوانین</span>
                <Icon name="chevronLeft" size={14} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
